import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Button, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const KurbanlikHisseFiyatlari = () => {
  document.title = "Kurbanlık Hisse Fiyatları | HarmanaGEL";

  const [fiyatlar, setFiyatlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [selectedFiyat, setSelectedFiyat] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const fetchFiyatlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/KURBANLIK_HISSE_FIYATLARI?$top=500&$orderby=ID desc&$expand=ILANLAR($select=AD)`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setFiyatlar(result.value);
    } catch (err) {
      setError("Fiyatlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFiyatlar();
  }, []);

  const handleOpenEdit = (fiyat) => {
    setSelectedFiyat(fiyat);
    setEditForm({
      KILO_ARALIGI: fiyat.KILO_ARALIGI || "",
      FIYAT: fiyat.FIYAT || "",
    });
    setEditModal(true);
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    try {
      await fetch(`${BASE_URL}/KURBANLIK_HISSE_FIYATLARI(${selectedFiyat.ID})`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          KILO_ARALIGI: editForm.KILO_ARALIGI,
          FIYAT: parseFloat(editForm.FIYAT) || 0,
        }),
      });
      setSuccessMsg("Fiyat güncellendi.");
      setEditModal(false);
      fetchFiyatlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Güncelleme sırasında hata oluştu.");
    } finally {
      setEditLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "İlan",
      accessorKey: "ILANLAR",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const i = cell.getValue();
        return i ? <span className="fw-medium">{i.AD}</span> : <span className="text-muted">İlan #{cell.row.original.ILAN_ID}</span>;
      },
    },
    {
      header: "Kilo Aralığı",
      accessorKey: "KILO_ARALIGI",
      enableSorting: true,
      enableColumnFilter: true,
      cell: (cell) => <span className="fw-medium">{cell.getValue() || "-"}</span>,
    },
    {
      header: "Fiyat",
      accessorKey: "FIYAT",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const v = cell.getValue();
        return v != null ? <span className="fw-bold text-success">{parseFloat(v).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</span> : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const f = cellProps.row.original;
        return (
          <Button color="primary" size="sm" outline onClick={() => handleOpenEdit(f)}>
            <i className="bx bx-edit me-1" /> Düzenle
          </Button>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Kurbanlık Hisse Fiyatları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Hisse Fiyatları</h4>
                    <Button color="primary" size="sm" onClick={fetchFiyatlar} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={fiyatlar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Fiyat ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={editModal} toggle={() => setEditModal(false)} centered>
        <ModalHeader toggle={() => setEditModal(false)}>
          Hisse Fiyatı Düzenle
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Kilo Aralığı</Label>
            <Input
              value={editForm.KILO_ARALIGI || ""}
              onChange={(e) => setEditForm({ ...editForm, KILO_ARALIGI: e.target.value })}
              placeholder="Örn: 200-250 kg"
            />
          </FormGroup>
          <FormGroup>
            <Label>Fiyat (₺)</Label>
            <Input
              type="number"
              step="0.01"
              value={editForm.FIYAT || ""}
              onChange={(e) => setEditForm({ ...editForm, FIYAT: e.target.value })}
              placeholder="0.00"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditModal(false)} disabled={editLoading}>İptal</Button>
          <Button color="primary" onClick={handleEditSave} disabled={editLoading}>
            {editLoading ? <><Spinner size="sm" className="me-1" />Kaydediliyor...</> : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default KurbanlikHisseFiyatlari;
